/**
 * Integrytas Group — Content Management System (CMS)
 * Page: CMS.ASSET.LIBRARY  —  Asset Library
 *
 * Upload images / documents / videos to Supabase Storage bucket `cms-assets`,
 * then record metadata in cms_asset. Grid supports filter by type, sort,
 * and inline alt-text editing via a right-side drawer.
 */
/* tl-group-ok: the listed row is a cms_asset (an uploaded file), which has no _tl. The
   embedded cms_asset_type_tl localises the asset TYPE shown in the Type column (§17.B4);
   an image has no translations of its own. */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { compareStrings, useSort } from '../../lib/use_sort';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import type { CmsAssetRow, CmsAssetTypeRow, CmsAssetSortKey } from '../../types/cms';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { UdcRegion } from '../../components/common/UdcRegion';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CMS.ASSET.LIBRARY';
registerUdcHost(PAGE_CODE, 'cms_asset', 'Assets');

const BUCKET = 'cms-assets';
const FILTER_KEY = 'cms.assets.filter';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'preview',           header: 'Preview',   defaultWidth: 60  },
  { key: 'original_filename', header: 'File Name', defaultWidth: 220 },
  { key: 'asset_type_code',   header: 'Type',      defaultWidth: 90  },
  { key: 'file_size_bytes',   header: 'Size',      defaultWidth: 90  },
  { key: 'alt_text_default',  header: 'Alt Text',  defaultWidth: 200 },
  { key: 'is_active',         header: 'Active',    defaultWidth: 80  },
];

export interface CmsAssetLibraryPageProps { session: AuthSession; }

export function CmsAssetLibraryPage({ session }: CmsAssetLibraryPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <CmsAssetLibraryPageInner session={session} />
    </PersonalizationProvider>
  );
}

function CmsAssetLibraryPageInner({ session }: CmsAssetLibraryPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]           = useState<CmsAssetRow[]>([]);
  const [filter, setFilter]       = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (Type / Active).
  const [facets, setFacets]       = useState<{ type: string[]; active: string[] }>({ type: [], active: ['Y'] });
  const [assetTypes, setAssetTypes] = useState<CmsAssetTypeRow[]>([]);
  // §17.B2: asset_type_code -> localized display_text (from cms_asset_type_tl).
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  const [editing, setEditing]     = useState<CmsAssetRow | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CmsAssetRow | null>(null);
  const sort = useSort<CmsAssetSortKey>('cms.assets.sort', { key: 'original_filename', dir: 'asc' });
  const udcGrid = useUdcGrid('cms_asset', PAGE_CODE, { pkColumn: 'asset_id', rows: rows as unknown as Record<string, unknown>[] });

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('cms_asset')
        .select('*')
        .order('created_at', { ascending: false });
      if (e) throw e;
      setRows((data ?? []) as CmsAssetRow[]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    void reload();
    const locale = activeLocale;
    void apiDb.from('cms_asset_type')
      .select('asset_type_code, sort_order, is_active, cms_asset_type_tl ( language_id, display_text )')
      .order('sort_order')
      .then(({ data }) => {
        type TlRow = { language_id: string; display_text: string };
        type TypeRow = CmsAssetTypeRow & { cms_asset_type_tl?: TlRow[] };
        const list = ((data ?? []) as TypeRow[]).map(r => {
          const tls = r.cms_asset_type_tl ?? [];
          const exact = tls.find(tl => tl.language_id === locale);
          const fb = tls.find(tl => tl.language_id === 'EN_US') ?? tls[0];
          return { ...r, display_text: exact?.display_text ?? fb?.display_text ?? r.asset_type_code };
        });
        setAssetTypes(list);
        const labels: Record<string, string> = {};
        for (const r of list) labels[r.asset_type_code] = r.display_text ?? r.asset_type_code;
        setTypeLabels(labels);
      });
  }, [reload, activeLocale]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const ext   = file.name.split('.').pop() ?? '';
      const stored = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await apiDb.storage.from(BUCKET).upload(stored, file);
      if (upErr) throw upErr;
      const { data: urlData } = apiDb.storage.from(BUCKET).getPublicUrl(stored);

      // Determine asset type from MIME
      const mime = file.type;
      let assetTypeCode = 'document';
      if (mime.startsWith('image/')) assetTypeCode = 'image';
      else if (mime.startsWith('video/')) assetTypeCode = 'video';
      else if (mime.startsWith('audio/')) assetTypeCode = 'audio';

      // Derive internal_code from filename (uppercase, replace non-alphanum with _)
      const internalCode = file.name.replace(/\.[^.]+$/, '').toUpperCase().replace(/[^A-Z0-9]/g, '_');

      // §2: route through the Common Routine (forensic stamp + audit + hooks).
      const { error: dbErr } = await routedInsert(session, 'cms_asset', {
        internal_code:    internalCode,
        asset_type_code:  assetTypeCode,
        original_filename: file.name,
        stored_filename:  stored,
        file_path:        urlData.publicUrl,
        mime_type:        mime,
        file_size_bytes:  file.size,
        is_active:        'Y',
      });
      if (dbErr) throw dbErr;
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (r: CmsAssetRow) => {
    setBusy(true); setError(null);
    try {
      await apiDb.storage.from(BUCKET).remove([r.stored_filename]);
      // §2: route through the Common Routine (forensic stamp + audit + hooks).
      const { error: e } = await routedDelete(session, 'cms_asset', 'asset_id', r.asset_id);
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const base = rows.filter(r => {
      const matchText = !filter.trim() ||
        r.original_filename.toLowerCase().includes(filter.toLowerCase()) ||
        r.internal_code.toLowerCase().includes(filter.toLowerCase());
      const matchType   = facets.type.length === 0 || facets.type.includes(r.asset_type_code);
      const matchActive = facets.active.length === 0 || facets.active.includes(r.is_active);
      return matchText && matchType && matchActive && udcGrid.matchRow(r as unknown as Record<string, unknown>);
    });
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      if (key === 'file_size_bytes') return (a.file_size_bytes ?? 0) - (b.file_size_bytes ?? 0);
      return compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? ''));
    });
  }, [rows, filter, facets, sort, udcGrid]);

  // §17.B5: distinct facet options. Type values are codes, labelled with their _tl display_text.
  const typeFacetOptions = assetTypes.map(at => ({ value: at.asset_type_code, label: typeLabels[at.asset_type_code] ?? at.asset_type_code }));
  const activeFacetOptions = [
    { value: 'Y', label: t('common.boolean.active', 'Active') },
    { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
  ];
  const anyFilterActive = filter.trim().length > 0 || facets.type.length + facets.active.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ type: [], active: [] }); };

  // §17.B13: expand/contract control. An asset has no obvious child collection loaded
  // here, so the control renders inert.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_id: string): RelatedChildGroup[] => [/* TODO: wire relations */];
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('cms.assets.title', 'Asset Library')}
          description={t('cms.assets.list.description', 'Upload and manage images, documents and media used across your content.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              <label className={`btn-primary flex cursor-pointer items-center gap-1.5 ${uploading ? 'cursor-wait opacity-70' : ''}`}>
                {uploading ? t('cms.assets.uploading', 'Uploading…') : t('cms.assets.upload')}
                <input type="file" className="sr-only" onChange={handleUpload} disabled={uploading}
                       accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" />
              </label>
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('cms.assets.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('cms.assets.search_placeholder', 'Search assets…')}
                className="h-8 w-64 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('cms.assets.col.asset_type_code', 'Type')} options={typeFacetOptions} selected={facets.type} onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('cms.assets.col.is_active', 'Active')} options={activeFacetOptions} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-csm-xs text-csm-muted">{t('cms.assets.count', '{{count}} asset', { count: filtered.length })}</span>
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('cms.assets.personalize_columns_hint', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-3 py-1.5 text-left" title={t('cms.assets.related.header', 'Related records')} />
                  {visibleCols.map(col => {
                    if (col.key === 'preview' || col.key === 'alt_text_default') {
                      return (
                        <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    }
                    return (
                      <SortableHeader<CmsAssetSortKey> key={col.key}
                        sortKey={col.key as CmsAssetSortKey} label={col.header}
                        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                        style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                    );
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.asset_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.asset_id)} hasChildren={hasRelated(r.asset_id)} onToggle={() => detail.toggle(r.asset_id)} expandLabel={t('cms.assets.related.expand', 'Show related records')} collapseLabel={t('cms.assets.related.collapse', 'Hide related')} tooltip={t('cms.assets.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className={'border-b border-csm-border-soft px-3 py-1.5' + (col.key === 'file_size_bytes' ? ' csm-num' : '')}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'preview' && (
                          r.asset_type_code === 'image'
                            ? <img src={r.file_path} alt={r.alt_text_default ?? ''} className="h-8 w-10 rounded object-cover border border-csm-border" />
                            : <span className="inline-block h-8 w-10 rounded border border-csm-border bg-csm-surface-alt text-center text-csm-2xs text-csm-muted leading-8">{r.asset_type_code.slice(0,3).toUpperCase()}</span>/* fk-display-ok: 3-char thumbnail placeholder; the type name is shown resolved elsewhere */
                        )}
                        {col.key === 'original_filename' && <span className="font-mono text-csm-sm">{r.original_filename}</span>}
                        {col.key === 'asset_type_code'   && <span className="text-csm-ink-soft">{typeLabels[r.asset_type_code] ?? r.asset_type_code}</span>}
                        {col.key === 'file_size_bytes'   && <span className="text-csm-ink-soft">{formatBytes(r.file_size_bytes)}</span>}
                        {col.key === 'alt_text_default'  && (
                          <span className="text-csm-ink-soft max-w-xs truncate block">
                            {r.alt_text_default ?? <span className="italic text-csm-muted">—</span>}
                          </span>
                        )}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key) && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }}
                              className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('common.edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }}
                              className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('common.delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.asset_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                      <RelatedChildrenPanel groups={buildRelatedGroups(r.asset_id)} session={session} />
                    </td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('cms.assets.no_results')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {editing && (
        <AssetEditModal
          session={session}
          asset={editing}
          typeLabel={typeLabels[editing.asset_type_code] ?? editing.asset_type_code}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('cms.assets.delete.title', 'Delete Asset?')}
          message={t('cms.assets.delete.message', 'Delete "{{name}}"? It will be removed from storage.', { name: deleteTarget.original_filename })}
          onConfirm={async () => { await handleDelete(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// EDIT MODAL
// ===========================================================================

interface AssetEditModalProps {
  session: AuthSession;
  asset: CmsAssetRow;
  typeLabel: string;
  onClose: () => void;
  onSaved: () => void;
}

function AssetEditModal({ session, asset, typeLabel, onClose, onSaved }: AssetEditModalProps) {
  const { isPersonalizing } = usePersonalization();
  const { t } = useTranslation();
  const CK = 'drawer';

  const initialValues = { altText: asset.alt_text_default ?? '', isActive: asset.is_active === 'Y' };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reset(initialValues); }, [asset]);

  const [showDiscard, setShowDiscard] = useState(false);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      // §2: route through the Common Routine (forensic stamp + audit + hooks).
      const { error: e } = await routedUpdate(session, 'cms_asset', 'asset_id', asset.asset_id, {
        alt_text_default: form.altText || null,
        is_active: form.isActive ? 'Y' : 'N',
      });
      if (e) throw e;
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        title={t('cms.assets.drawer.edit_title', 'Edit Asset — {{name}}', { name: asset.original_filename })}
        onClose={handleClose}
        width="lg"
        headerAction={<PersonalizationToggle />}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {isPersonalizing && (
            <div className="rounded border border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('cms.assets.personalize_fields_hint', 'Toggle fields — hide or mark required. Click icon to save.')}
            </div>
          )}
          {/* Preview */}
          {asset.asset_type_code === 'image' && (
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.assets.drawer.preview', 'Preview')}</label>
              <img src={asset.file_path} alt={asset.alt_text_default ?? ''} className="max-h-40 rounded border border-csm-border object-contain" />
            </div>
          )}
          {/* Read-only metadata */}
          <div className="rounded border border-csm-border bg-csm-surface-alt p-3 text-csm-sm space-y-1">
            <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.original_filename')}</span><span className="font-mono text-csm-xs">{asset.original_filename}</span></div>
            <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.asset_type_code')}</span><span>{typeLabel}</span></div>
            <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.mime_type')}</span><span className="font-mono text-csm-xs">{asset.mime_type}</span></div>
            <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.file_size_bytes')}</span><span>{formatBytes(asset.file_size_bytes)}</span></div>
            {asset.width_px && <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.width_px')}</span><span>{asset.width_px} × {asset.height_px}px</span></div>}
            <div className="flex gap-2"><span className="w-28 text-csm-muted">{t('cms.assets.drawer.fields.public_url')}</span>
              <a href={asset.file_path} target="_blank" rel="noreferrer" className="truncate font-mono text-csm-2xs text-primary hover:underline" style={{ maxWidth: '18rem' }}>{asset.file_path}</a>
            </div>
          </div>
          <PersonalizedField componentKey={CK} fieldKey="alt_text_default" label={t('cms.assets.drawer.fields.alt_text_default')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.assets.drawer.fields.alt_text_default')}</label>
            <textarea rows={2} value={form.altText} onChange={e => setForm(f => ({ ...f, altText: e.target.value }))}
                      placeholder={t('cms.assets.drawer.fields.alt_text_placeholder', 'Describe the image for screen readers…')}
                      className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
          </PersonalizedField>
          <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('cms.assets.drawer.fields.is_active')}>
            <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 accent-primary" />
              {t('cms.assets.drawer.fields.is_active')}
            </label>
          </PersonalizedField>
          <UdcRegion entity="cms_asset" pageCode={PAGE_CODE} pkColumn="asset_id" pk={asset?.asset_id} />
          {error && (
            <ErrorBanner
              message={t('cms.assets.drawer.save_failed', "Couldn't save the asset.")}
              detail={error}
              details_label={t('common.details', 'Details')}
            />
          )}
        </div>
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_changes.title', 'Discard Changes?')}
          message={t('common.discard_changes.message', 'You have unsaved changes. Discard them and close?')}
          confirmLabel={t('common.discard', 'Discard')}
          onConfirm={() => { setShowDiscard(false); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
